import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import useQueryParams from 'hooks/useQueryParams';
import {
  BOSS_MODE_TRACK_EVENTS,
  BOSS_MODE_TRACK_EVENTS_LABEL,
  BossModeStatsFilters,
  BossModeTrack,
  DEAL_FILTER_KEY,
  DEAL_TITLE_FILTER_KEY,
  EVENT_FILTER_KEY,
  LEAD_ID_FILTER_KEY,
  LEAD_NAME_FILTER_KEY,
  NEW_NDA_STATUS_FILTER_KEY,
  TIMEFRAME_FILTER,
  USER_ID_FILTER_KEY,
  USER_NAME_FILTER_KEY,
} from '../../types';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import useGetFilterOptions from 'hooks/useGetFilterOptions';
import useGetSearchableFilterOptions from '../../hooks';
import {
  BossModeTrackTableKeys,
  CUSTOM_COL_SIZES,
  HEADING_TRANSLATIONS,
  MULTI_SORT_VALUES,
  SORTING_VALUES,
  TABLE_COLUMNS,
} from '../../components/BossModeStatsTableCells/data';
import { BODY_CONTENT_GENERATE } from '../../components/BossModeStatsTableCells';
import { FilterOption } from 'models/Filters';
import { SORTING_ORDER, SortingDirectionValues } from 'models/Sort';
import { ColumnFilterOption } from 'components/ReorderResizeTable/types';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import { TIMEFRAME_PARAM_KEYWORD } from 'data/global';
import api from 'api';
import ReorderResizeTable from 'components/ReorderResizeTable';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import MultiFilter from 'components/MultiFilter';
import MultiSort from 'components/MultiSort';

import './Forensics.styles.scss';

type ForensicsProps = {
  className?: string;
};

const Forensics: React.FC<ForensicsProps> = (props) => {
  const { className } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState<BossModeTrack[]>([]);
  const [totalPages, setTotalPages] = useState(0);

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const {
    params: {
      page,
      sort,
      eventType,
      newNdaStatus,
      previousNdaStatus,
      userId,
      dealRoomIds,
      buyerIds,
      timeframe,
    },
  } = useQueryParams<BossModeStatsFilters & { sort: string }>();

  const classes = classNames('forensics', className);

  const { t } = useTranslation();

  const createSelectOptions = useCreateSelectOptions();
  const { getMultiSelectSearchableFilter } = useGetFilterOptions();

  const { ndaOptions, getDealOptions, getLeadsOptions, getBMUserOptions } =
    useGetSearchableFilterOptions();

  const renderedContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (stat: BossModeTrack) => BODY_CONTENT_GENERATE[col](stat),
        }),
        {},
      ),
    [],
  );

  const eventTypeFilterOption = useMemo<FilterOption>(
    () => ({
      value: EVENT_FILTER_KEY,
      label: t('eventType'),
      isMultiple: true,
      options: Object.values(BOSS_MODE_TRACK_EVENTS).map((value) => ({
        value,
        label: t(BOSS_MODE_TRACK_EVENTS_LABEL[value]),
      })),
    }),
    [t],
  );

  const timeFilterOption = useMemo<FilterOption>(
    () => ({
      value: TIMEFRAME_PARAM_KEYWORD,
      label: t('time'),
      shouldSortOptions: false,
      options: createSelectOptions(Object.values(TIMEFRAME_FILTER)),
    }),
    [createSelectOptions, t],
  );

  const filterOptions = useMemo(
    () =>
      [
        eventTypeFilterOption,
        getMultiSelectSearchableFilter(
          t('bossMode'),
          USER_ID_FILTER_KEY,
          USER_NAME_FILTER_KEY,
          getBMUserOptions,
        ),
        getMultiSelectSearchableFilter(
          t('lead'),
          LEAD_ID_FILTER_KEY,
          LEAD_NAME_FILTER_KEY,
          getLeadsOptions,
        ),
        getMultiSelectSearchableFilter(
          t('deal'),
          DEAL_FILTER_KEY,
          DEAL_TITLE_FILTER_KEY,
          getDealOptions,
        ),
        {
          value: NEW_NDA_STATUS_FILTER_KEY,
          label: t('ndaStatus'),
          isMultiple: true,
          options: ndaOptions,
        },
        timeFilterOption,
      ] as FilterOption[],
    [
      eventTypeFilterOption,
      ndaOptions,
      timeFilterOption,
      t,
      getMultiSelectSearchableFilter,
      getBMUserOptions,
      getLeadsOptions,
      getDealOptions,
    ],
  );

  const translatedSortOptions = useMemo(
    () =>
      MULTI_SORT_VALUES.map(({ value, label }) => ({ value, label: t(label) })),
    [t],
  );

  const multiSortDirectionTranslation = useMemo<SortingDirectionValues>(
    () => ({
      newNdaStatus: {
        [SORTING_ORDER.A_TO_Z]: t('leastProgressedFirst'),
        [SORTING_ORDER.Z_TO_A]: t('mostProgressedFirst'),
      },
      createdAt: {
        [SORTING_ORDER.A_TO_Z]: t('oldestFirst'),
        [SORTING_ORDER.Z_TO_A]: t('recentFirst'),
      },
    }),
    [t],
  );

  const columnFilterOptions = useMemo(
    () =>
      ({
        eventType: {
          options: eventTypeFilterOption,
        },
        user: {
          options: getMultiSelectSearchableFilter(
            t('bossMode'),
            USER_ID_FILTER_KEY,
            USER_NAME_FILTER_KEY,
            getBMUserOptions,
          ),
        },
        leadNames: {
          options: getMultiSelectSearchableFilter(
            t('lead'),
            LEAD_ID_FILTER_KEY,
            LEAD_NAME_FILTER_KEY,
            getLeadsOptions,
          ),
        },
        dealRoomNames: {
          options: getMultiSelectSearchableFilter(
            t('deal'),
            DEAL_FILTER_KEY,
            DEAL_TITLE_FILTER_KEY,
            getDealOptions,
          ),
        },
        previousNdaStatus: {
          options: {
            value: NEW_NDA_STATUS_FILTER_KEY,
            label: t('ndaStatus'),
            isMultiple: true,
            options: ndaOptions,
          },
          sortDirectionTranslation: multiSortDirectionTranslation.newNdaStatus,
        },
        createdAt: {
          options: timeFilterOption,
          sortDirectionTranslation: multiSortDirectionTranslation.createdAt,
        },
      } as Partial<Record<BossModeTrackTableKeys[number], ColumnFilterOption>>),
    [
      multiSortDirectionTranslation,
      eventTypeFilterOption,
      ndaOptions,
      timeFilterOption,
      getMultiSelectSearchableFilter,
      t,
      getBMUserOptions,
      getLeadsOptions,
      getDealOptions,
    ],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const {
          data: { items, totalPages },
        } = await api.portfolio.admin.getBossModeTracks({
          $page: page,
          $limit: limit,
          $sort: sort,
          eventType,
          newNdaStatus,
          previousNdaStatus,
          userId,
          dealRoomIds,
          buyerIds,
          timeframe,
        });
        setItems(items);
        setTotalPages(totalPages);
      } catch (e) {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    page,
    limit,
    sort,
    eventType,
    newNdaStatus,
    previousNdaStatus,
    userId,
    dealRoomIds,
    buyerIds,
    timeframe,
  ]);

  return (
    <div className={classes}>
      <div>
        <ActiveFilterSortPills
          filterOptions={filterOptions}
          sortOptions={translatedSortOptions}
        />

        <div className="filter-wrapper">
          <NumberOfResultsPerPageFilter
            limit={limit}
            updateLimit={updateLimit}
          />
          <MultiFilter filterOptions={filterOptions} />
          <MultiSort
            multiSortValues={translatedSortOptions}
            multiSortDirectionTranslation={multiSortDirectionTranslation}
          />
        </div>
      </div>

      <ReorderResizeTable
        columns={TABLE_COLUMNS}
        data={items}
        isDragDisabled
        isDataLoading={isLoading}
        totalPages={totalPages}
        customSizes={CUSTOM_COL_SIZES}
        headingTranslations={HEADING_TRANSLATIONS}
        tableContentTransformations={renderedContent}
        sortingValues={SORTING_VALUES}
        columnFilterOptions={columnFilterOptions}
      />
    </div>
  );
};

export default Forensics;
